/**
 * 课程商品管理 API（course-product-catalog:SC-01~SC-43 前端）。
 *
 * 对齐后端 product-catalog 模块 ProductController REST 契约：
 * - GET    /products                          -> Paginated<ProductItem>（筛选 + 分页）
 * - GET    /products/:id                      -> ProductItem（详情）
 * - POST   /products                          -> ProductItem（新建，初始 DRAFT）
 * - PATCH  /products/:id                      -> ProductItem（编辑，乐观锁 version，SKU 配置整体覆盖）
 * - POST   /products/:id/publish              -> ProductItem（上架，DRAFT/OFF_SHELF -> ON_SHELF）
 * - POST   /products/:id/unpublish            -> ProductItem（下架）
 * - DELETE /products/:id                      -> { deleted: true }（守卫：上架中/被订单引用 409）
 * - POST   /products/cover-upload-credentials -> CoverCredentialResult（凭证 + 不可猜测 key）
 * - GET    /products/:id/sales-history        -> Paginated<SalesHistoryItem>（一期空态，4-1 注入）
 *
 * 路径从 api.config.ts 导入，禁止内联；金额单位为「分」（int），前端展示除以 100 转元。
 */
import { ApiPaths } from './api.config';
import { http } from './transport';
import type { Paginated } from './campuses.api';
import type { ShelfStatus } from './course-skus.api';

/**
 * 商品 SKU 配置行售卖形态（O-14 判别联合）：课时包按课时购买 / 期限卡按时间购买。
 * 存量数据（chargeMode 下沉前）缺省按课时包渲染。
 */
export type SkuChargeMode = 'SESSION_PACKAGE' | 'PERIOD';

/** 商品-SKU 配置视图（含 SKU/科目名与标价冗余；O-14 additive 判别联合字段）。 */
export interface ProductSkuConfigItem {
  id: string;
  skuId: string;
  skuName?: string;
  subjectName?: string;
  /** 售卖形态（历史数据缺省=课时包） */
  chargeMode?: SkuChargeMode;
  /** 课时形态：商品内课时数（期限形态无值） */
  lessonCount?: number;
  /** 期限形态：期限天数（到期日 = 激活日 + periodDays − 1，含当天） */
  periodDays?: number;
  /**
   * 行标价（分）：课时形态 = SKU 课时标价；期限形态 = 卡面标价
   * （原价合计口径 = Σ行标价，期限行取卡面标价）
   */
  listPrice?: number;
  /** 生效试课赠送次数（商品覆盖值优先，未覆盖取 SKU 当前值） */
  trialSessionCount?: number;
  /** 商品内试课次数覆盖存储值（null=跟随 SKU 当前值；编辑回填区分跟随/固化） */
  trialSessionCountOverride?: number | null;
  sortOrder: number;
}

/** 课程商品视图（列表项与详情共用）。 */
export interface ProductItem {
  id: string;
  organizationId: string;
  /** 校区归属（issue #7；null/缺省 = 机构通用，全校区可见） */
  campusId?: string | null;
  provinceId: string;
  provinceName: string;
  cityId: string;
  cityName: string;
  name: string;
  coverImage?: string | null;
  /** 封面访问 URL（经 ObjectStorage resolveAccess 解析；未上传为 null） */
  coverImageUrl?: string | null;
  /** 商品定价（分）；订单应收口径 */
  price: number;
  /** 原价（Σ SKU 标价合计，视图派生） */
  originalPrice: number;
  /** 首付款比例（0~1 闭区间） */
  depositRatio: number;
  /** 尾款比例（= 1 - depositRatio，视图派生） */
  finalRatio: number;
  /** 尾款周期（天，>= 1） */
  finalDueDays: number;
  /** 卖点标签（三端一致展示） */
  features: string[];
  status: ShelfStatus;
  publishedAt?: string | null;
  /** 订单引用数（一期占位 0，4-1 回填） */
  orderRefCount: number;
  skuConfigs: ProductSkuConfigItem[];
  /** 乐观锁版本号 */
  version: number;
  updatedAt: string;
}

/** 商品列表查询参数（skuId 为「包含 SKU」筛选）。 */
export interface ProductListQuery {
  name?: string;
  provinceId?: string;
  cityId?: string;
  status?: ShelfStatus;
  skuId?: string;
  /** 校区归属筛选（issue #7）：返回该校区商品 + 机构通用商品；缺省不筛 */
  campusId?: string;
  page?: number;
  pageSize?: number;
}

/**
 * SKU 配置输入（新建/编辑随附；同商品内 skuId 不可重复，服务端校验）。
 *
 * O-14 判别联合：按 chargeMode 携带判别字段——课时行 `lessonCount`（服务端互斥
 * CHECK 为兜底），期限行 `periodDays` + `listPrice`（卡面标价，分）。
 */
export interface ProductSkuConfigInput {
  skuId: string;
  /** 售卖形态（缺省=课时包，兼容历史提交） */
  chargeMode?: SkuChargeMode;
  /** 课时形态：商品内课时数（必填 ≥1） */
  lessonCount?: number;
  /** 期限形态：期限天数（必填 ≥1） */
  periodDays?: number;
  /** 期限形态：卡面标价（分，必填 >0） */
  listPrice?: number;
  /** 试课赠送次数覆盖（1~3 整数；缺省或 null=跟随 SKU 当前值） */
  trialSessionCount?: number | null;
  sortOrder?: number;
}

/** 新建课程商品请求体（无 finalRatio/orderRefCount/status——均由服务端管理）。 */
export interface CreateProduct {
  /** 校区归属（issue #7；null/缺省 = 机构通用；须为 ACTIVE 校区且属本机构） */
  campusId?: string | null;
  provinceId: string;
  provinceName: string;
  cityId: string;
  cityName: string;
  name: string;
  coverImage?: string;
  price: number;
  /** 0~1 闭区间（前端 0~100% 换算） */
  depositRatio: number;
  finalDueDays?: number;
  features?: string[];
  skuConfigs: ProductSkuConfigInput[];
}

/** 编辑课程商品请求体（同 Create 可编辑字段 + 乐观锁 version）。 */
export interface UpdateProduct extends CreateProduct {
  version: number;
}

/** 封面上传凭证签发响应（fake 模式 directUpload=false 前端跳过真实 PUT，design D6）。 */
export interface CoverCredentialResult {
  credentials: {
    secretId: string;
    secretKey: string;
    sessionToken: string;
    expiredAt: string;
    allowedPrefix: string;
    /** 直传目标桶/地域（服务端凭证下发，real 模式直传必需） */
    bucket?: string;
    region?: string;
  };
  /** 不可猜测对象 key（product-covers/<随机段>.<扩展名>，上传后回填 coverImage） */
  objectKey: string;
  /** adapters real 时 true：要求客户端用临时密钥直传 COS */
  directUpload: boolean;
}

/** 销售历史订单条目（契约冻结，4-1 注入真实数据源；监护人手机号脱敏）。 */
export interface SalesHistoryItem {
  orderId: string;
  orderNo: string;
  studentName: string;
  guardianMobileMasked: string;
  campusName: string;
  /** 应收（分） */
  receivableAmount: number;
  /** 实收（分） */
  paidAmount: number;
  status: string;
  source: string;
  createdAt: string;
}

/** 构造带筛选与分页的商品列表查询串。 */
function buildListQuery(query: ProductListQuery): string {
  const params = new URLSearchParams();
  if (query.name) params.set('name', query.name);
  if (query.provinceId) params.set('provinceId', query.provinceId);
  if (query.cityId) params.set('cityId', query.cityId);
  if (query.status) params.set('status', query.status);
  if (query.skuId) params.set('skuId', query.skuId);
  if (query.campusId) params.set('campusId', query.campusId);
  if (query.page) params.set('page', String(query.page));
  if (query.pageSize) params.set('pageSize', String(query.pageSize));
  const qs = params.toString();
  return qs ? `${ApiPaths.products.list}?${qs}` : ApiPaths.products.list;
}

async function listProducts(query: ProductListQuery = {}): Promise<Paginated<ProductItem>> {
  return http.get<Paginated<ProductItem>>(buildListQuery(query));
}

async function getProduct(id: string): Promise<ProductItem> {
  return http.get<ProductItem>(ApiPaths.products.detail(id));
}

async function createProduct(dto: CreateProduct): Promise<ProductItem> {
  return http.post<ProductItem>(ApiPaths.products.create, dto);
}

async function updateProduct(id: string, dto: UpdateProduct): Promise<ProductItem> {
  return http.patch<ProductItem>(ApiPaths.products.update(id), dto);
}

async function publishProduct(id: string): Promise<ProductItem> {
  return http.post<ProductItem>(ApiPaths.products.publish(id), {});
}

async function unpublishProduct(id: string): Promise<ProductItem> {
  return http.post<ProductItem>(ApiPaths.products.unpublish(id), {});
}

async function removeProduct(id: string): Promise<{ deleted: true }> {
  return http.delete<{ deleted: true }>(ApiPaths.products.remove(id));
}

async function issueCoverUploadCredentials(filename: string): Promise<CoverCredentialResult> {
  return http.post<CoverCredentialResult>(ApiPaths.products.coverUploadCredentials, { filename });
}

async function listSalesHistory(
  id: string,
  page = 1,
  pageSize = 20,
): Promise<Paginated<SalesHistoryItem>> {
  return http.get<Paginated<SalesHistoryItem>>(
    `${ApiPaths.products.salesHistory(id)}?page=${page}&pageSize=${pageSize}`,
  );
}

/** 课程商品 API 唯一对外入口。 */
export const productApi = {
  listProducts,
  getProduct,
  createProduct,
  updateProduct,
  publishProduct,
  unpublishProduct,
  removeProduct,
  issueCoverUploadCredentials,
  listSalesHistory,
};
